// client/src/utils/moodUtils.js - COMPLETE Utility functions for mood analytics and sentiment analysis

/**
 * Get color code for different risk levels
 * @param {string} riskLevel - The risk level (LOW, MILD, MODERATE, HIGH, CRISIS, POSITIVE)
 * @returns {string} Color code
 */
export const getRiskLevelColor = (riskLevel) => {
  const colors = {
    POSITIVE: '#4CAF50',     // Green
    LOW: '#8BC34A',          // Light green
    MILD: '#FFC107',         // Yellow
    MODERATE: '#FF9800',     // Orange
    HIGH: '#F44336',         // Red
    CRISIS: '#D32F2F',       // Dark red
    UNKNOWN: '#9E9E9E'       // Gray
  };
  
  return colors[riskLevel] || colors.UNKNOWN;
};

/**
 * Get display text for risk levels
 * @param {string} riskLevel - The risk level
 * @returns {string} Human-readable display text
 */
export const getRiskLevelText = (riskLevel) => {
  const texts = {
    POSITIVE: 'Positive',
    LOW: 'Low Risk',
    MILD: 'Mild Risk',
    MODERATE: 'Moderate Risk',
    HIGH: 'High Risk',
    CRISIS: 'Crisis',
    UNKNOWN: 'Unknown'
  };
  
  return texts[riskLevel] || texts.UNKNOWN;
};

/**
 * Format sentiment data for clinical display
 * @param {Object} sentimentData - Raw sentiment analysis data
 * @returns {Object} Formatted sentiment data for UI display
 */
export const formatSentimentForClinicalDisplay = (sentimentData) => {
  if (!sentimentData) {
    return {
      hasData: false,
      display: 'No sentiment analysis available',
      riskLevel: 'UNKNOWN',
      color: getRiskLevelColor('UNKNOWN')
    };
  }

  return {
    hasData: true,
    sentiment: {
      type: sentimentData.type || 'neutral',
      score: sentimentData.score || 0,
      confidence: sentimentData.confidence || 0,
      display: `${sentimentData.type || 'neutral'} (${sentimentData.score || 0}%)`
    },
    riskLevel: {
      level: sentimentData.riskLevel || 'UNKNOWN',
      text: getRiskLevelText(sentimentData.riskLevel),
      color: getRiskLevelColor(sentimentData.riskLevel),
      requiresAttention: ['HIGH', 'CRISIS'].includes(sentimentData.riskLevel)
    },
    phq9Analysis: sentimentData.phq9Analysis ? {
      symptomCount: sentimentData.phq9Analysis.symptomCount || 0,
      symptoms: sentimentData.phq9Analysis.detectedSymptoms || [],
      clinicalScore: sentimentData.phq9Analysis.clinicalScore || 0,
      meetsDepressionCriteria: sentimentData.phq9Analysis.meetsDepressionCriteria || false,
      hasRecoveryIndicators: sentimentData.phq9Analysis.hasRecoveryIndicators || false,
      display: `${sentimentData.phq9Analysis.symptomCount || 0} symptoms detected`
    } : null,
    moodValidation: sentimentData.moodValidation ? {
      status: sentimentData.moodValidation.status || 'UNKNOWN',
      message: sentimentData.moodValidation.message || 'No validation performed',
      concern: sentimentData.moodValidation.concern || 'none',
      isConsistent: sentimentData.moodValidation.status === 'CONSISTENT',
      wasOverridden: sentimentData.moodValidation.moodOverridden || false
    } : null,
    clinicalNotes: sentimentData.clinicalNotes || 'No clinical notes available',
    recommendedAction: sentimentData.recommendedAction || 'continue_monitoring',
    analyzedAt: sentimentData.analyzedAt || new Date()
  };
};

/**
 * Format chart data for mood and sentiment visualization
 * @param {Array} moodData - Array of mood entries
 * @param {string} chartType - Type of chart ('mood', 'sentiment', 'risk', 'trend')
 * @returns {Object} Formatted chart data
 */
export const formatChartData = (moodData, chartType = 'mood') => {
  if (!moodData || moodData.length === 0) {
    return {
      labels: [],
      datasets: [],
      isEmpty: true
    };
  }

  switch (chartType) {
    case 'mood':
      return formatMoodChartData(moodData);
    case 'sentiment':
      return formatSentimentChartData(moodData);
    case 'risk':
      return formatRiskChartData(moodData);
    case 'trend':
      return formatTrendChartData(moodData);
    default:
      return formatMoodChartData(moodData);
  }
};

/**
 * Format mood chart data
 * @param {Array} moodData - Array of mood entries
 * @returns {Object} Chart data for mood visualization
 */
const formatMoodChartData = (moodData) => {
  const labels = moodData.map(entry => 
    new Date(entry.timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })
  );
  
  const moodRatings = moodData.map(entry => entry.moodRating || 0);
  
  return {
    labels,
    datasets: [
      {
        label: 'Mood Rating',
        data: moodRatings,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }
    ],
    isEmpty: false
  };
};

/**
 * Format sentiment chart data
 * @param {Array} moodData - Array of mood entries with sentiment data
 * @returns {Object} Chart data for sentiment visualization
 */
const formatSentimentChartData = (moodData) => {
  const sentimentData = moodData.filter(entry => entry.sentiment);
  
  if (sentimentData.length === 0) {
    return {
      labels: [],
      datasets: [],
      isEmpty: true
    };
  }

  const labels = sentimentData.map(entry => 
    new Date(entry.timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })
  );
  
  const sentimentScores = sentimentData.map(entry => entry.sentiment.score || 0);
  const riskColors = sentimentData.map(entry => 
    getRiskLevelColor(entry.sentiment.riskLevel)
  );
  
  return {
    labels,
    datasets: [
      {
        label: 'Sentiment Score',
        data: sentimentScores,
        borderColor: '#2196F3',
        backgroundColor: riskColors.map(color => color + '20'), // Add transparency
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }
    ],
    isEmpty: false
  };
};

/**
 * Format risk level chart data
 * @param {Array} moodData - Array of mood entries with sentiment data
 * @returns {Object} Chart data for risk level visualization
 */
const formatRiskChartData = (moodData) => {
  const sentimentData = moodData.filter(entry => entry.sentiment);
  
  if (sentimentData.length === 0) {
    return {
      labels: [],
      datasets: [],
      isEmpty: true
    };
  }

  // Count risk levels
  const riskCounts = sentimentData.reduce((acc, entry) => {
    const risk = entry.sentiment.riskLevel || 'UNKNOWN';
    acc[risk] = (acc[risk] || 0) + 1;
    return acc;
  }, {});

  const labels = Object.keys(riskCounts).map(risk => getRiskLevelText(risk));
  const data = Object.values(riskCounts);
  const colors = Object.keys(riskCounts).map(risk => getRiskLevelColor(risk));

  return {
    labels,
    datasets: [
      {
        label: 'Risk Level Distribution',
        data,
        backgroundColor: colors,
        borderColor: colors.map(color => color + 'CC'), // Slightly transparent border
        borderWidth: 1
      }
    ],
    isEmpty: false
  };
};

/**
 * Format trend chart data
 * @param {Array} moodData - Array of mood entries
 * @returns {Object} Chart data for trend visualization
 */
const formatTrendChartData = (moodData) => {
  if (moodData.length === 0) {
    return {
      labels: [],
      datasets: [],
      isEmpty: true
    };
  }

  const labels = moodData.map(entry => 
    new Date(entry.timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })
  );
  
  const moodRatings = moodData.map(entry => entry.moodRating || 0);
  
  // Calculate 7-day moving average
  const movingAverage = [];
  for (let i = 0; i < moodRatings.length; i++) {
    const start = Math.max(0, i - 6);
    const slice = moodRatings.slice(start, i + 1);
    const avg = slice.reduce((sum, val) => sum + val, 0) / slice.length;
    movingAverage.push(Math.round(avg * 100) / 100);
  }

  const datasets = [
    {
      label: 'Daily Mood',
      data: moodRatings,
      borderColor: '#4CAF50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      borderWidth: 1,
      fill: false,
      tension: 0.1
    },
    {
      label: '7-Day Average',
      data: movingAverage,
      borderColor: '#2196F3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
      borderWidth: 2,
      fill: false,
      tension: 0.4
    }
  ];

  // Add sentiment data if available
  const sentimentData = moodData.filter(entry => entry.sentiment);
  if (sentimentData.length > 0) {
    const sentimentScores = moodData.map(entry => 
      entry.sentiment ? entry.sentiment.score / 20 : null // Scale to 1-5 range
    );
    
    datasets.push({
      label: 'Sentiment Score',
      data: sentimentScores,
      borderColor: '#FF9800',
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      borderWidth: 1,
      fill: false,
      tension: 0.3,
      spanGaps: true
    });
  }

  return {
    labels,
    datasets,
    isEmpty: false
  };
};

/**
 * Calculate mood statistics
 * @param {Array} moodData - Array of mood entries
 * @returns {Object} Calculated statistics
 */
export const calculateMoodStatistics = (moodData) => {
  if (!moodData || moodData.length === 0) {
    return {
      totalEntries: 0,
      averageRating: 0,
      moodScore: 0,
      trend: 'stable',
      mostFrequentMood: null,
      sentimentStats: null
    };
  }

  const totalEntries = moodData.length;
  const averageRating = moodData.reduce((sum, entry) => sum + entry.moodRating, 0) / totalEntries;
  const moodScore = Math.round(((averageRating - 1) / 4) * 100);

  // Calculate trend (comparing first half vs second half)
  const midPoint = Math.floor(totalEntries / 2);
  const firstHalf = moodData.slice(0, midPoint);
  const secondHalf = moodData.slice(midPoint);

  const firstHalfAvg = firstHalf.length > 0 
    ? firstHalf.reduce((sum, entry) => sum + entry.moodRating, 0) / firstHalf.length 
    : 0;
  const secondHalfAvg = secondHalf.length > 0 
    ? secondHalf.reduce((sum, entry) => sum + entry.moodRating, 0) / secondHalf.length 
    : 0;

  const trend = secondHalfAvg > firstHalfAvg ? 'improving' : 
                secondHalfAvg < firstHalfAvg ? 'declining' : 'stable';

  // Most frequent mood
  const moodCounts = moodData.reduce((acc, entry) => {
    acc[entry.moodKey] = (acc[entry.moodKey] || 0) + 1;
    return acc;
  }, {});

  const mostFrequentMood = Object.entries(moodCounts)
    .sort(([,a], [,b]) => b - a)[0];

  // Sentiment statistics
  const sentimentData = moodData.filter(entry => entry.sentiment);
  let sentimentStats = null;

  if (sentimentData.length > 0) {
    const avgSentimentScore = sentimentData.reduce((sum, entry) => 
      sum + entry.sentiment.score, 0) / sentimentData.length;

    const riskCounts = sentimentData.reduce((acc, entry) => {
      const risk = entry.sentiment.riskLevel || 'UNKNOWN';
      acc[risk] = (acc[risk] || 0) + 1;
      return acc;
    }, {});

    const highRiskCount = (riskCounts.HIGH || 0) + (riskCounts.CRISIS || 0);

    sentimentStats = {
      totalAnalyzed: sentimentData.length,
      averageScore: Math.round(avgSentimentScore),
      riskDistribution: riskCounts,
      highRiskCount,
      analysisCompleteness: Math.round((sentimentData.length / totalEntries) * 100)
    };
  }

  return {
    totalEntries,
    averageRating: Math.round(averageRating * 100) / 100,
    moodScore,
    trend,
    mostFrequentMood: mostFrequentMood ? {
      mood: mostFrequentMood[0],
      count: mostFrequentMood[1],
      percentage: Math.round((mostFrequentMood[1] / totalEntries) * 100)
    } : null,
    sentimentStats
  };
};

/**
 * Format mood data for export
 * @param {Array} moodData - Array of mood entries
 * @param {boolean} includeSentiment - Whether to include sentiment analysis
 * @returns {Object} Formatted data for export
 */
export const formatMoodDataForExport = (moodData, includeSentiment = true) => {
  if (!moodData || moodData.length === 0) {
    return {
      headers: [],
      rows: [],
      isEmpty: true
    };
  }

  const baseHeaders = [
    'Date',
    'Time',
    'Mood Rating',
    'Mood Label',
    'Reflection'
  ];

  const sentimentHeaders = includeSentiment ? [
    'Sentiment Type',
    'Sentiment Score',
    'Risk Level',
    'PHQ-9 Symptoms',
    'Clinical Notes'
  ] : [];

  const headers = [...baseHeaders, ...sentimentHeaders];

  const rows = moodData.map(entry => {
    const date = new Date(entry.timestamp);
    const baseRow = [
      date.toLocaleDateString(),
      date.toLocaleTimeString(),
      entry.moodRating,
      entry.moodLabel,
      entry.reflection || 'No reflection'
    ];

    const sentimentRow = includeSentiment && entry.sentiment ? [
      entry.sentiment.type || 'Unknown',
      entry.sentiment.score || 0,
      entry.sentiment.riskLevel || 'Unknown',
      entry.sentiment.phq9Analysis?.symptomCount || 0,
      entry.sentiment.clinicalNotes || 'No notes'
    ] : includeSentiment ? [
      'No analysis',
      'N/A',
      'N/A',
      'N/A',
      'No analysis performed'
    ] : [];

    return [...baseRow, ...sentimentRow];
  });

  return {
    headers,
    rows,
    isEmpty: false
  };
};

/**
 * Get mood SVG URL based on mood key
 * @param {string} moodKey - The mood key
 * @returns {string} SVG URL for the mood
 */
export const getMoodEmoji = (moodKey) => {
  const moodSvgUrls = {
    great: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_Great_anfp2e.svg',
    good: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_good_ldcpo4.svg',
    okay: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_okay_tcf9cp.svg',
    struggling: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_struggling_regocn.svg',
    upset: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_upset_pqskxp.svg'
  };
  
  return moodSvgUrls[moodKey] || moodSvgUrls['okay'];
};

/**
 * Get mood label with emoji (updated to return just the label since emoji is now an SVG)
 * @param {string} moodKey - The mood key
 * @param {string} moodLabel - The mood label
 * @returns {string} Mood label
 */
export const getMoodLabelWithEmoji = (moodKey, moodLabel) => {
  // Since we're using SVG URLs now, just return the label
  // The SVG should be rendered separately as an img tag
  return moodLabel;
};

/**
 * Validate mood data
 * @param {Object} moodData - Mood data to validate
 * @returns {Object} Validation result
 */
export const validateMoodData = (moodData) => {
  const errors = [];
  
  if (!moodData) {
    errors.push('Mood data is required');
    return { isValid: false, errors };
  }

  if (!moodData.moodRating || moodData.moodRating < 1 || moodData.moodRating > 5) {
    errors.push('Mood rating must be between 1 and 5');
  }

  if (!moodData.moodKey || !['great', 'good', 'okay', 'struggling', 'upset'].includes(moodData.moodKey)) {
    errors.push('Invalid mood key');
  }

  if (!moodData.moodLabel || moodData.moodLabel.trim().length === 0) {
    errors.push('Mood label is required');
  }

  if (moodData.reflection && moodData.reflection.length > 1000) {
    errors.push('Reflection text is too long (max 1000 characters)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Default export with all functions
// eslint-disable-next-line import/no-anonymous-default-export
export default {
  getRiskLevelColor,
  getRiskLevelText,
  formatSentimentForClinicalDisplay,
  formatChartData,
  calculateMoodStatistics,
  formatMoodDataForExport,
  getMoodEmoji,
  getMoodLabelWithEmoji,
  validateMoodData
};